import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Locale, LocalizedText } from '../types'

const dictionary = {
  ko: {
    serviceName: 'Animal Care', serviceSubtitle: '반려동물 건강기록', overview: '반려동물', timeline: '날짜순 기록', media: '사진과 영상', documents: '진료자료', questions: '물어볼 내용',
    updated: '최근 업데이트', visit: '내원 예정', allPets: '두 고양이', source: '기록 근거', measured: '실측', observed: '보호자 관찰', attention: '확인 필요', stable: '호전 관찰', ongoing: '지속 중', improving: '완화 중', resolved: '해결', unknown: '확인 중',
    weight: '체중', listed: '공고상', clinicMeasured: '병원 실측', appetite: '식욕', feeding: '급여', stool: '배변', personality: '생활과 성격', adoption: '입양 정보', registration: '동물등록', insurance: '보험',
    medicalNote: '이 페이지는 보호자가 관찰한 사실과 기존 기록을 진료진에게 정확히 전달하기 위한 자료입니다. 진단과 치료 판단은 담당 수의사가 진행합니다.',
    urgentHeading: '집에서 달라진 점', urgentBody: '보호자가 집에서 본 변화를 적은 내용입니다.',
    statusAvailable: '확인 가능', statusPending: '업로드 예정', statusMissing: '준비 필요', noFile: '파일이 연결되면 이 자리에서 바로 열립니다.', upload: '파일 추가', filterAll: '전체',
    language: '언어', appearance: '화면 모드', system: '시스템', light: '화이트', dark: '다크', share: '공유', print: '인쇄', menu: '메뉴', close: '닫기',
    guardianCare: '보호 환경', profile: '기본 정보', records: '측정 기록', symptoms: '현재 증상', clinicHistory: '진료와 투약 기록', prepareVisit: '진료 전 준비', selectPet: '고양이 선택',
    login: '로그인', email: '이메일', password: '비밀번호', superAdmin: '최고 관리자', hospitalAdmin: '병원 관리자', member: '회원', signOut: '로그아웃', preview: '화면 미리보기',
  },
  en: {
    serviceName: 'Animal Care', serviceSubtitle: 'Pet Health Records', overview: 'Clinical summary', timeline: 'Timeline', media: 'Photos & video', documents: 'Documents', questions: 'Visit checklist',
    updated: 'Last updated', visit: 'Scheduled visit', allPets: 'Both kittens', source: 'Record source', measured: 'Measured', observed: 'Guardian observation', attention: 'Needs review', stable: 'Improving', ongoing: 'Ongoing', improving: 'Improving', resolved: 'Resolved', unknown: 'Under review',
    weight: 'Weight', listed: 'Listing', clinicMeasured: 'Clinic measurement', appetite: 'Appetite', feeding: 'Feeding', stool: 'Stool', personality: 'Behavior', adoption: 'Adoption', registration: 'Registration', insurance: 'Insurance',
    medicalNote: 'This page organizes guardian observations and existing records for accurate clinical handoff. Diagnosis and treatment decisions remain with the attending veterinarian.',
    urgentHeading: 'Priority items for this visit', urgentBody: 'Current symptoms and changes selected by the guardian are shown by clinical review priority.',
    statusAvailable: 'Available', statusPending: 'Upload planned', statusMissing: 'Preparation needed', noFile: 'The file will open here once connected.', upload: 'Add file', filterAll: 'All',
    language: 'Language', appearance: 'Appearance', system: 'System', light: 'Light', dark: 'Dark', share: 'Share', print: 'Print', menu: 'Menu', close: 'Close',
    guardianCare: 'Care setting', profile: 'Profile', records: 'Measurements', symptoms: 'Current symptoms', clinicHistory: 'Visits & medication', prepareVisit: 'Visit preparation', selectPet: 'Select kitten',
    login: 'Sign in', email: 'Email', password: 'Password', superAdmin: 'Super admin', hospitalAdmin: 'Hospital admin', member: 'Member', signOut: 'Sign out', preview: 'Preview portal',
  },
} as const

type Dictionary = typeof dictionary.ko
interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  tr: Dictionary
  text: (value: LocalizedText | string) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => (localStorage.getItem('animal-care-locale') as Locale) || 'ko')
  const setLocale = (next: Locale) => {
    localStorage.setItem('animal-care-locale', next)
    setLocaleState(next)
  }
  useEffect(() => { document.documentElement.lang = locale }, [locale])
  const value = useMemo(() => ({ locale, setLocale, tr: dictionary[locale] as Dictionary, text: (item: LocalizedText | string) => typeof item === 'string' ? item : item[locale] }), [locale])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) throw new Error('useI18n must be used inside I18nProvider')
  return context
}
